/**
 * The curves the layouts route edges along.
 *
 * Edges are polylines by the time a renderer sees them, so every curve here ends in a sampled point
 * list. Sampling on the CPU rather than in a shader because the sample count depends on the curve's
 * length and its curvature, which the layout knows and a vertex shader would have to be told.
 */

import type { Vec3 } from '../types';

const lerp = (from: number, to: number, t: number): number =>
  from + (to - from) * t;

/**
 * Holten's bundling strength, applied to a control polygon before it is smoothed.
 *
 * `beta` of 1 leaves the polygon alone, so an edge hugs the hierarchy all the way up to the common
 * ancestor and back - maximum bundling, and at the limit unreadable, because every edge in a bundle
 * is drawn on top of every other. 0 replaces the polygon with the straight line between its ends,
 * which is no bundling at all. The useful range is about 0.6 to 0.9: enough that edges between the
 * same two subtrees travel together, not so much that an individual edge cannot be followed.
 */
export const straighten = (controls: readonly Vec3[], beta: number): Vec3[] => {
  if (controls.length < 2) return [...controls];

  const first = controls[0];
  const last = controls[controls.length - 1];
  const lastIndex = controls.length - 1;

  return controls.map(([x, y, z], index) => {
    const t = index / lastIndex;

    return [
      lerp(lerp(first[0], last[0], t), x, beta),
      lerp(lerp(first[1], last[1], t), y, beta),
      lerp(lerp(first[2], last[2], t), z, beta),
    ];
  });
};

/**
 * A uniform cubic B-spline over the control points, clamped to both ends.
 *
 * B-spline rather than an interpolating curve: the control points are the hierarchy's own nodes, and
 * a curve that passed exactly through each one would run through the middle of the parent
 * component's mark on the way past. Approximating them instead gives a curve that leans toward the
 * ancestor without touching it, which is what a bundle should look like.
 *
 * The ends are clamped by repeating the first and last control point to multiplicity three, which
 * is what a cubic needs: the basis at t = 0 weights three consecutive controls 1/6, 4/6, 1/6, so
 * all three have to be the same point for the curve to pass through it. Repeating them only once
 * starts the curve at `(5 * first + second) / 6` - a sixth of the way to the next control, which
 * on screen is an edge that visibly does not touch its source node.
 */
export const sampleBSpline = (
  controls: readonly Vec3[],
  samples: number
): Vec3[] => {
  if (controls.length < 2) return [...controls];
  if (controls.length === 2) return [controls[0], controls[1]];

  const first = controls[0];
  const last = controls[controls.length - 1];
  const padded: Vec3[] = [first, first, ...controls, last, last];
  const segments = padded.length - 3;
  const count = Math.max(2, samples);

  return Array.from({ length: count }, (_, index): Vec3 => {
    const position = (index / (count - 1)) * segments;
    const segment = Math.min(segments - 1, Math.floor(position));
    const t = position - segment;

    const [p0, p1, p2, p3] = [
      padded[segment],
      padded[segment + 1],
      padded[segment + 2],
      padded[segment + 3],
    ];

    /* The cubic B-spline basis, written out rather than assembled from a matrix so the weights are
       readable: they sum to 1 at every t, which is what keeps the curve inside the convex hull of
       its control points. */
    const t2 = t * t;
    const t3 = t2 * t;
    const w0 = (1 - 3 * t + 3 * t2 - t3) / 6;
    const w1 = (4 - 6 * t2 + 3 * t3) / 6;
    const w2 = (1 + 3 * t + 3 * t2 - 3 * t3) / 6;
    const w3 = t3 / 6;

    return [
      p0[0] * w0 + p1[0] * w1 + p2[0] * w2 + p3[0] * w3,
      p0[1] * w0 + p1[1] * w1 + p2[1] * w2 + p3[1] * w3,
      p0[2] * w0 + p1[2] * w1 + p2[2] * w2 + p3[2] * w3,
    ];
  });
};

/**
 * A quadratic Bezier, for a single-bend edge.
 *
 * What the tree layout draws its reference edges as: a straight chord across a radial tree crosses
 * the structure at an arbitrary angle and reads as noise, while the same edge bent toward the axis
 * separates visibly from the tree's own radial lines.
 */
export const sampleQuadratic = (
  from: Vec3,
  control: Vec3,
  to: Vec3,
  samples: number
): Vec3[] => {
  const count = Math.max(2, samples);

  return Array.from({ length: count }, (_, index): Vec3 => {
    const t = index / (count - 1);
    const inverse = 1 - t;
    const a = inverse * inverse;
    const b = 2 * inverse * t;
    const c = t * t;

    return [
      from[0] * a + control[0] * b + to[0] * c,
      from[1] * a + control[1] * b + to[1] * c,
      from[2] * a + control[2] * b + to[2] * c,
    ];
  });
};
